import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Heart, Users, Award, Shield, Target, Sparkles } from 'lucide-react';

const values = [
  {
    icon: Heart,
    title: "Partner-First",
    description: "Your success is our success. Every decision we make is guided by what's best for our partners and their clients."
  },
  {
    icon: Award,
    title: "White-Glove Excellence",
    description: "We believe in delivering premium service at every touchpoint. No shortcuts, no compromises."
  },
  {
    icon: Shield,
    title: "Trust & Integrity",
    description: "We protect your brand as if it were our own. Professional, compliant, and always representing you with excellence."
  },
  {
    icon: Target,
    title: "Results-Driven",
    description: "We're not just about activity—we're about outcomes. Every action is designed to help you grow your business."
  }
];

const team = [
  {
    name: "Founder & CEO",
    role: "Leadership",
    description: "With over 15 years in the mortgage industry, our founder saw firsthand the gap between loan closing and ongoing client relationships. Better Closings was born from a passion to help mortgage professionals deliver exceptional client experiences."
  },
  {
    name: "Operations Team",
    role: "Client Success",
    description: "Our dedicated operations team brings decades of combined experience in customer service and mortgage operations. They ensure every client interaction reflects the premium standard our partners expect."
  },
  {
    name: "Concierge Team",
    role: "Client Experience",
    description: "Our concierge specialists are trained in professional communication, relationship building, and the nuances of the mortgage industry. They're the voice of your brand to your clients."
  }
];

export const AboutPage = () => {
  return (
    <div data-testid="about-page" className="min-h-screen bg-[#0F0F0F]">
      {/* Hero Section */}
      <section className="pt-32 pb-20 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="h-[2px] w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Our Story</p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6">
              About <span className="text-[#E5D5A5]">Better Closings</span>
            </h1>
            <p className="text-lg text-neutral-300 leading-relaxed">
              We're on a mission to elevate the post-closing experience for mortgage 
              professionals and their clients—delivering white-glove service at scale.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section data-testid="mission-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">Our Mission</p>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
                Elevate Your <span className="text-[#E5D5A5]">Experience</span>
              </h2>
              <p className="text-neutral-300 text-lg leading-relaxed mb-6">
                At Better Closings, we believe that the relationship between a mortgage 
                professional and their client shouldn't end at the closing table. In fact, 
                that's when it becomes most valuable.
              </p>
              <p className="text-neutral-400 leading-relaxed mb-6">
                Our mission is to help mortgage brokers, loan officers, and lenders 
                deliver an exceptional post-closing experience that drives reviews, 
                strengthens retention, and generates referrals—without adding work to 
                their already demanding schedules.
              </p>
              <p className="text-neutral-400 leading-relaxed">
                We handle the follow-up so you can focus on what you do best: closing 
                more loans and building your business.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="absolute -inset-4 bg-[#C9A227]/5 blur-3xl" />
              <img
                src="https://images.pexels.com/photos/8276364/pexels-photo-8276364.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Better Closings team"
                className="relative w-full h-[500px] object-cover border border-white/10"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section data-testid="values-section" className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">What We Stand For</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              Our <span className="text-[#E5D5A5]">Values</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`value-${value.title.toLowerCase().replace(/\s+/g, '-')}`}
                className="bg-[#1A1A1A] border border-white/5 p-8 text-center group hover:border-[#C9A227]/30 transition-all duration-500"
              >
                <value.icon className="w-12 h-12 text-[#C9A227] mx-auto mb-6 group-hover:scale-110 transition-transform" strokeWidth={1} />
                <h3 className="font-serif text-xl text-white mb-3">{value.title}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section data-testid="team-section" className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-[#C9A227] text-xs tracking-[0.2em] uppercase mb-4">The People Behind Better Closings</p>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white">
              Our <span className="text-[#E5D5A5]">Team</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-[#1A1A1A] border border-white/5 p-8 hover:border-[#C9A227]/30 transition-all duration-500"
              >
                <div className="w-16 h-16 bg-[#C9A227]/20 rounded-full flex items-center justify-center mb-6">
                  <Users className="w-8 h-8 text-[#C9A227]" strokeWidth={1.5} />
                </div>
                <p className="text-[#C9A227] text-xs tracking-wider uppercase mb-2">{member.role}</p>
                <h3 className="font-serif text-xl text-white mb-4">{member.name}</h3>
                <p className="text-neutral-400 text-sm leading-relaxed">{member.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Promise */}
      <section className="py-24 bg-[#141414] border-y border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="w-12 h-12 text-[#C9A227] mx-auto mb-8" strokeWidth={1} />
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Our <span className="text-[#E5D5A5]">Promise</span>
            </h2>
            <p className="text-neutral-300 text-xl leading-relaxed mb-8">
              "Every client interaction we handle on your behalf will reflect the same 
              commitment to excellence that you bring to every loan you close. 
              Your reputation is our responsibility."
            </p>
            <div className="h-px w-16 bg-[#C9A227] mx-auto mb-8" />
            <p className="text-[#C9A227] font-serif text-lg">The Better Closings Team</p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
              Ready to <span className="text-[#E5D5A5]">Work Together?</span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10">
              Let us show you how Better Closings can elevate your client experience 
              and help you grow your business.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="about-cta-primary">
                <Button className="bg-[#C9A227] text-black hover:bg-[#E5D5A5] rounded-none px-10 py-6 font-bold text-sm tracking-wider uppercase transition-all duration-300">
                  Book a Demo
                </Button>
              </Link>
              <Link to="/how-it-works" data-testid="about-cta-secondary">
                <Button variant="outline" className="bg-transparent border border-[#C9A227] text-[#C9A227] hover:bg-[#C9A227]/10 rounded-none px-10 py-6 font-semibold text-sm tracking-wider uppercase transition-all duration-300">
                  See How It Works
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
